import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import dynamic from "next/dynamic";
import { Col, Row, Container } from "react-bootstrap";
import Link from "next/link";
import moment from "moment-timezone";
import dummyImg from "../../../public/images/blog-1.jpg";
import { healerServices } from "@/services/healerServices";
import Layout from "@/components/layout";
import styles from "@/styles/BlogDetail.module.css";
import Head from "next/head";
import { useRouter } from "next/router";

const OwlCarousel = dynamic(() => import("react-owl-carousel"), {
  ssr: false,
});

const BlogDetail = (props) => {
  useEffect(() => {
    setDetail(props.detail);
    setSliderData(props?.otherBlogs);
  }, [props]);

  const [detail, setDetail] = useState(props.detail ?? {});
  const [sliderData, setSliderData] = useState(props?.otherBlogs ?? []);
  const router = useRouter();
  const [showSideButton, setShowSideButton] = useState(false);
  const firstSectionRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!firstSectionRef.current) return;

      const sectionTop = firstSectionRef.current.getBoundingClientRect().bottom;

      setShowSideButton(sectionTop < 0);
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const validateImgUrl = (str) => {
    var pattern = new RegExp(
      "^(https?:\\/\\/)?" + // protocol
        "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // domain name
        "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
        "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
        "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
        "(\\#[-a-z\\d_]*)?$",
      "i"
    ); // fragment locator
    return !!pattern.test(str);
  };

  return (
    <>
      <Head>
        <title>{detail?.og_title}</title>
        <meta name="title" content={detail?.meta_title}></meta>
        <meta name="description" content={detail?.meta_description}></meta>
        <meta name="keywords" content={detail?.meta_keywords}></meta>
        <meta property="og:type" content="website"></meta>
        <meta property="og:title" content={detail?.og_title}></meta>
        <meta property="og:image" content={detail?.og_image}></meta>
        <link
          rel="canonical"
          href={`https://www.zenkify.com${router.asPath}`}
        ></link>
        <meta
          property="og:description"
          content={detail?.meta?.meta_description}
        ></meta>
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={detail?.meta?.meta_title} />
        <meta
          name="twitter:description"
          content={detail?.meta?.meta_description}
        />
        <meta name="twitter:image" content={detail?.meta?.og_image} />
        <meta
          property="og:url"
          content={`https://www.zenkify.com${router.asPath}`}
        />
        <meta property="og:site_name" content="Zenkify" />
        <meta property="og:image:alt" content="zenkify blogs" />
        <meta property="og:image" content={detail?.meta?.og_image}></meta>
      </Head>
      {detail ? (
        <section className={styles["blog-details-section"]}>
          <Container>
            <Row>
              <Col md={12} className="m-auto">
                <div className={styles["blog-single-post"]}>
                  <div className={styles["inner-box"]}>
                    <div className={styles["image"]} ref={firstSectionRef}>
                      <Image
                        src={
                          validateImgUrl(detail?.thumb)
                            ? detail?.thumb
                            : dummyImg
                        }
                        alt={detail?.meta_title}
                        onError={"/images/blog-1.jpg"}
                        width={1232}
                        height={400}
                      />
                    </div>
                    <ul className={styles["post-meta"]}>
                      <li>
                        <i className="far fa-user"></i>{" "}
                        {detail?.user?.first_name && detail?.user?.last_name
                          ? `${detail?.user?.first_name} ${detail?.user?.last_name}`
                          : "Not Specified"}
                      </li>
                      <li>
                        <i className="fas fa-calendar-alt"></i>
                        {detail?.created_at
                          ? moment.tz(detail.updated_at, "UTC").format("LL")
                          : "Not Specified"}
                      </li>
                    </ul>
                    <div className={styles["blog-content"]}>
                      <h1>{detail?.title ?? "Not Available"}</h1>
                      <blockquote>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: detail?.description ?? "Not Available",
                          }}
                        />
                      </blockquote>
                    </div>
                  </div>
                </div>
              </Col>

              {sliderData.length ? (
                <section className={styles["blog-section"]}>
                  <Row>
                    <div className=" m-auto">
                      <div className="section-title">
                        <h3 className="title">
                          Our Latest & Most Popular Tips & Tricks For You
                        </h3>
                        <p>
                          Enhance Your Practice with Expert Advice. Discover New
                          Techniques, Insights, and Best Practices for Reiki.
                          Stay Updated with Our Latest Tips & Tricks for Optimal
                          Healing Results.
                        </p>
                      </div>
                    </div>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <>
                        <OwlCarousel
                          className={styles["blog-slider owl-theme"]}
                          loop={false}
                          margin={10}
                          nav={false}
                          dots={true}
                          responsive={{
                            0: {
                              items: 1,
                            },
                            600: {
                              items: 2,
                            },
                            991: {
                              items: 3,
                            },
                            1000: {
                              items: 4,
                            },
                          }}
                        >
                          {sliderData?.map((item) => {
                            return (
                              <>
                                <div className={styles["item"]} key={item.id}>
                                  <div className={styles["card-blog"]}>
                                    <div className={styles["blog-image"]}>
                                      <Image
                                        height={400}
                                        width={298}
                                        src={
                                          validateImgUrl(item?.og_image)
                                            ? item?.og_image
                                            : "/images/blog-1.jpg"
                                        }
                                        alt="blog"
                                      />
                                    </div>
                                    <div className={styles["blog-content"]}>
                                      <div className={styles["blog-date"]}>
                                        {moment(item.updated_at).format(
                                          "MMM Do YYYY"
                                        )}
                                      </div>
                                      <h4>
                                        {item?.title
                                          ? item?.title?.length > 25
                                            ? `${item?.title?.slice(
                                                0,
                                                60
                                              )}.....`
                                            : item?.title
                                          : "Not Specified"}
                                      </h4>
                                      <Link
                                        href={`/blogs/${item?.slug}`}
                                        className={styles["theme-btn"]}
                                        onClick={() => {
                                          setSliderData([]);
                                          setDetail({});
                                        }}
                                      >
                                        Read More
                                      </Link>
                                    </div>
                                  </div>
                                </div>
                              </>
                            );
                          })}
                        </OwlCarousel>
                      </>
                    </Col>
                  </Row>
                </section>
              ) : (
                ""
              )}
            </Row>
          </Container>
        </section>
      ) : (
        ""
      )}
      <Link
        id="floatingBook"
        className="floating-book"
        aria-label="Book a FREE call now"
        href="/healers"
        style={{ opacity: showSideButton ? 1 : 0 }}
      >
        Book a FREE call now
      </Link>
    </>
  );
};

export default BlogDetail;

BlogDetail.getLayout = function getLayout(page) {
  return <Layout>{page}</Layout>;
};

// ssr
export async function getServerSideProps(context) {
  const { query } = context;
  const fetchBlogDetail = async () => {
    const res = await healerServices.getBlogDetail(query.slug);
    if (res.status) {
      return res.data;
    } else {
      toast.error(res.message, {
        autoClose: 5000,
      });
    }
  };

  const { blogDetails, otherBlogs } = (await fetchBlogDetail()) ?? {};
  // Pass data to the page via props
  return { props: { detail: blogDetails, otherBlogs: otherBlogs } };
}
